/**
 * KPI SWR Cache Implementation
 * 
 * Stale-While-Revalidate cache for KPI data with:
 * - Instant cache hits (no skeleton on revisit)
 * - Background refresh for stale data
 * - Persistence to localStorage/Capacitor
 * - Environment flag to disable (VITE_KPI_CACHE_ENABLED)
 * 
 * See docs/KPI-SWR-CACHE-IMPLEMENTATION.md for full documentation.
 */

import type {
  CacheEntry,
  SWROptions,
  SWRResult,
  CachedData,
  KpiCacheConfig,
  CacheStorage,
} from './kpiCacheTypes';

const DEFAULT_STALE_TIME = 60 * 60 * 1000; // 1 hour
const DEFAULT_STORAGE_KEY = 'kpi-swr-cache';
const DEFAULT_MAX_ENTRIES = 500;

/**
 * Check if cache is enabled via environment variable.
 * Default: enabled (true)
 */
const getInitialCacheState = (): boolean => {
  try {
    if (typeof import.meta !== 'undefined' && (import.meta as any).env) {
      return (import.meta as any).env.VITE_KPI_CACHE_ENABLED !== 'false';
    }
  } catch {
    // Fallback for environments without import.meta
  }
  return true;
};

/**
 * Default storage using localStorage (works in browser and Capacitor WebView)
 */
const defaultStorage: CacheStorage = {
  getItem: (key: string) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key: string, value: string) => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.warn('[KpiCache] Storage setItem failed:', e);
    }
  },
  removeItem: (key: string) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore
    }
  },
};

/**
 * KPI SWR Cache Class
 * 
 * Provides Stale-While-Revalidate caching for KPI API calls.
 */
export class KpiSWRCache {
  private cache = new Map<string, CacheEntry>();
  private inFlight = new Map<string, Promise<unknown>>();
  private enabled: boolean;
  private config: Required<KpiCacheConfig>;

  constructor(config: KpiCacheConfig = {}) {
    this.enabled = getInitialCacheState();
    this.config = {
      storageKey: config.storageKey ?? DEFAULT_STORAGE_KEY,
      maxEntries: config.maxEntries ?? DEFAULT_MAX_ENTRIES,
      defaultStaleTime: config.defaultStaleTime ?? DEFAULT_STALE_TIME,
      storage: config.storage ?? defaultStorage,
    };

    // Load persisted cache on initialization
    this.load();
  }

  // ==================== Public API ====================

  /**
   * Get data with SWR strategy.
   * 
   * - Cache hit (fresh): Returns cached data immediately
   * - Cache hit (stale): Returns cached data + triggers background refresh
   * - Cache miss: Fetches fresh data
   * 
   * @param key - Cache key (typically URL + normalized query params)
   * @param fetcher - Async function to fetch fresh data
   * @param options - SWR options
   */
  async getWithSWR<T>(
    key: string,
    fetcher: () => Promise<T>,
    options: SWROptions = {}
  ): Promise<SWRResult<T>> {
    const { staleTime = this.config.defaultStaleTime, onBackgroundUpdate } = options;

    // Bypass cache when disabled (for testing/debugging)
    if (!this.enabled) {
      // console.log('[KpiCache] BYPASS (cache disabled):', key);
      const data = await fetcher();
      return { data, fromCache: false, isRevalidating: false };
    }

    const cached = this.cache.get(key);
    const now = Date.now();

    // Cache hit
    if (cached && now < cached.expiresAt) {
      const isStale = now > cached.staleAfter;

      // console.log(`[KpiCache] HIT (${isStale ? 'stale' : 'fresh'}):`, key);

      // Background revalidation if stale
      if (isStale && !this.inFlight.has(key)) {
        this.revalidateInBackground(key, fetcher, cached.data as T, staleTime, onBackgroundUpdate);
      }

      return { 
        data: cached.data as T, 
        fromCache: true, 
        isRevalidating: isStale && this.inFlight.has(key) 
      };
    }

    // Cache miss - fetch and cache
    // console.log('[KpiCache] MISS:', key);
    const data = await this.fetchAndCache(key, fetcher, staleTime);
    return { data, fromCache: false, isRevalidating: false };
  }

  /**
   * Get cached data without fetching.
   * Useful for checking cache state before dispatching thunks.
   */
  getCached<T>(key: string): CachedData & { data: T | null } {
    const cached = this.cache.get(key);
    const now = Date.now();

    if (!cached) {
      return { data: null, exists: false, isStale: false, isExpired: true };
    }

    return {
      data: cached.data as T,
      exists: true,
      isStale: now > cached.staleAfter,
      isExpired: now > cached.expiresAt,
    };
  }

  /**
   * Manually set cache entry.
   * Only caches if cache is enabled and data is not null/undefined.
   */
  set<T>(key: string, data: T, staleTime: number = this.config.defaultStaleTime): void {
    if (!this.enabled || data === null || data === undefined) {
      return;
    }

    const now = Date.now();
    const ttl = this.calculateTTL(key);

    this.cache.set(key, {
      data,
      cachedAt: now,
      staleAfter: now + staleTime,
      expiresAt: ttl === Infinity ? Number.MAX_SAFE_INTEGER : now + ttl,
    });

    // LRU eviction if over limit
    if (this.cache.size > this.config.maxEntries) {
      this.evictOldest();
    }

    this.persist();
    // console.log('[KpiCache] SET:', key);
  }

  /**
   * Invalidate (remove) a specific cache entry.
   */
  invalidate(key: string): void {
    this.cache.delete(key);
    this.persist();
    // console.log('[KpiCache] INVALIDATE:', key);
  }

  /**
   * Invalidate all entries matching a pattern.
   * @param pattern - String to match in cache keys (e.g., '/site/energy/')
   */
  invalidatePattern(pattern: string): void {
    let count = 0;
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
        count++;
      }
    }
    this.persist();
    // console.log(`[KpiCache] INVALIDATE PATTERN "${pattern}": ${count} entries`);
  }

  /**
   * Clear all cache entries.
   */
  clear(): void {
    this.cache.clear();
    this.config.storage.removeItem(this.config.storageKey);
    // console.log('[KpiCache] CLEAR: all entries removed');
  }

  // ==================== Runtime Toggle ====================

  /**
   * Enable or disable cache at runtime.
   */
  setEnabled(value: boolean): void {
    this.enabled = value;
    // console.log(`[KpiCache] Cache ${value ? 'enabled' : 'disabled'}`);
  }

  /**
   * Check if cache is currently enabled.
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Get current cache size (number of entries).
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * Get cache statistics.
   */
  stats(): { enabled: boolean; entries: number; keys: string[] } {
    return {
      enabled: this.enabled,
      entries: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }

  // ==================== Private Methods ====================

  private async revalidateInBackground<T>(
    key: string,
    fetcher: () => Promise<T>,
    currentData: T,
    staleTime: number,
    onUpdate?: (newData: unknown) => void
  ): Promise<void> {
    const request = fetcher();
    this.inFlight.set(key, request);

    try {
      const newData = await request;
      this.set(key, newData, staleTime);

      // Notify if data changed (deep comparison via JSON)
      const hasChanged = JSON.stringify(newData) !== JSON.stringify(currentData);
      if (hasChanged) {
        // console.log('[KpiCache] BACKGROUND UPDATE (data changed):', key);
        onUpdate?.(newData);
      } else {
        // console.log('[KpiCache] BACKGROUND UPDATE (no change):', key);
      }
    } catch (error) {
      // Silent fail - user still has cached data
      console.warn('[KpiCache] Background revalidation failed:', key, error);
    } finally {
      this.inFlight.delete(key);
    }
  }

  private async fetchAndCache<T>(
    key: string,
    fetcher: () => Promise<T>,
    staleTime: number
  ): Promise<T> {
    // Deduplicate in-flight requests
    if (this.inFlight.has(key)) {
      // console.log('[KpiCache] DEDUPE (waiting for in-flight):', key);
      return this.inFlight.get(key) as Promise<T>;
    }

    const request = fetcher();
    this.inFlight.set(key, request);

    try {
      const data = await request;
      // Only cache successful responses (data exists)
      if (data !== null && data !== undefined) {
        this.set(key, data, staleTime);
      }
      return data;
    } finally {
      this.inFlight.delete(key);
    }
  }

  /**
   * Calculate TTL based on data recency (extracted from cache key).
   * - Historical data (>30 days): permanent
   * - Recent data (1-30 days): 24 hours
   * - Today's data: 5 minutes
   */
  private calculateTTL(key: string): number {
    const match = key.match(/dateTo=([^&]+)/);
    if (!match) return 24 * 60 * 60 * 1000; // Default 24h

    try {
      const dateTo = new Date(decodeURIComponent(match[1]));
      const now = new Date();
      const daysDiff = (now.getTime() - dateTo.getTime()) / (1000 * 60 * 60 * 24);

      if (daysDiff > 30) return Infinity; // Historical → permanent
      if (daysDiff > 1) return 24 * 60 * 60 * 1000; // Recent → 24h
      return 5 * 60 * 1000; // Today → 5 min
    } catch {
      return 24 * 60 * 60 * 1000; // Default 24h on parse error
    }
  }

  // ==================== Persistence ====================

  private persist(): void {
    try {
      const serializable: Record<string, CacheEntry> = {};
      for (const [key, entry] of this.cache) {
        serializable[key] = entry;
      }
      this.config.storage.setItem(this.config.storageKey, JSON.stringify(serializable));
    } catch (e) {
      console.warn('[KpiCache] Persist failed:', e);
    }
  }

  private load(): void {
    try {
      const stored = this.config.storage.getItem(this.config.storageKey);
      if (stored && typeof stored === 'string') {
        const parsed = JSON.parse(stored);
        this.cache = new Map(Object.entries(parsed));
        this.evictExpired();
        // console.log(`[KpiCache] Loaded ${this.cache.size} entries from storage`);
      }
    } catch (e) {
      console.warn('[KpiCache] Load failed:', e);
    }
  }

  private evictExpired(): void {
    const now = Date.now();
    let evicted = 0;
    for (const [key, entry] of this.cache) {
      if (now > entry.expiresAt) {
        this.cache.delete(key);
        evicted++;
      }
    }
    if (evicted > 0) {
      // console.log(`[KpiCache] Evicted ${evicted} expired entries`);
    }
  }

  private evictOldest(): void {
    // LRU: evict oldest entry (first in Map iteration order)
    const oldestKey = this.cache.keys().next().value;
    if (oldestKey) {
      this.cache.delete(oldestKey);
      // console.log('[KpiCache] LRU evicted:', oldestKey);
    }
  }
}

// ==================== Singleton Instance ====================

export const kpiCache = new KpiSWRCache();

// ==================== DevTools API ====================

if (typeof window !== 'undefined') {
  (window as any).__KPI_CACHE__ = {
    disable: () => kpiCache.setEnabled(false),
    enable: () => kpiCache.setEnabled(true),
    clear: () => kpiCache.clear(),
    status: () => console.log('Cache enabled:', kpiCache.isEnabled()),
    stats: () => console.log(kpiCache.stats()),
    invalidate: (key: string) => kpiCache.invalidate(key),
    invalidatePattern: (pattern: string) => kpiCache.invalidatePattern(pattern),
  };
}

// ==================== Utility Functions ====================

/**
 * Build a normalized cache key from URL and params.
 * Parameters are sorted alphabetically for consistent keys.
 */
export function buildCacheKey(
  url: string,
  params: Record<string, string | number | boolean | undefined>
): string {
  const sortedParams = Object.keys(params)
    .filter((key) => params[key] !== undefined)
    .sort()
    .map((key) => `${key}=${encodeURIComponent(String(params[key]))}`)
    .join('&');

  return sortedParams ? `${url}?${sortedParams}` : url;
}

